import { FormattedMessage } from 'components/shared/Intl';
import { useSPIntl } from 'hooks/useIntl';
import { FC, useCallback, useContext } from 'react';
import { Button } from 'react-bootstrap';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import { getNewGuid } from 'utils/guid';

import type { ColumnDefinition } from '../DailyBlotter.model';
import type { DailyBlotterRowProps } from './row.connect';
import { connectDailyBlotterRow } from './row.connect';

const Component: FC<DailyBlotterRowProps> = ({
  dailyBlotterColumnsDefinition,
  dailyBlotterQuote,
  onReprice,
  quoteId,
}) => {
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);
  const handleReprice = useCallback(() => {
    logAnalytics.trackEvent(logAnalyticsCategory, 'Reprice dailyBlotter');
    onReprice(getNewGuid() as string, dailyBlotterQuote);
  }, [onReprice, dailyBlotterQuote, logAnalyticsCategory]);

  return (
    <>
      {dailyBlotterColumnsDefinition.map((header: ColumnDefinition, headerIndex: number) => {
        return (
          <td key={headerIndex} data-e2e={`dailyBlotterRow-${header.field}`}>
            <FormatedCell quoteRow={dailyBlotterQuote} header={header} />
          </td>
        );
      })}
      <td key={quoteId + 'repriceButton'} data-e2e={`reprice-button-${quoteId}`}>
        <Button onClick={handleReprice} disabled={!dailyBlotterQuote.canReprice} variant="secondary">
          <FormattedMessage id="dailyBlotter.ReloadButton" />
        </Button>
      </td>
    </>
  );
};

const FormatedCell: FC<{ quoteRow: any; header: ColumnDefinition }> = ({ quoteRow, header }) => {
  const intl = useSPIntl();
  const value = quoteRow[header.field];
  if (value === undefined) {
    return null;
  }

  switch (header.type) {
    case 'date':
      return <>{intl.formatDisplayedDate(value)}</>;
    case 'datetime':
      return (
        <>
          {intl.formatDate(value, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
          })}
        </>
      );
    case 'percent':
      return <>{intl.formatNumber(value, { minimumFractionDigits: 4 })} %</>;
    case 'number':
      return <>{intl.formatNumber(value)}</>;
    case 'string':
      return (
        <>
          {intl.formatMessage({
            id: `dailyBlotter.grid.${quoteRow.productSubtype}.${header.field}.${value}` as any,
            defaultMessage: value,
          })}
        </>
      );
    default:
      return value;
  }
};

export const DailyBlotterRow = connectDailyBlotterRow(Component);
Component.displayName = 'DailyBlotterRow';
